import React from 'react';
import { cn } from '@/lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Activity,
  Calendar,
  CreditCard,
  DollarSign,
  FileText,
  Shield,
  UserCheck,
  Users,
} from 'lucide-react';
import { SummaryCardData, ReportSection, SectionIconMap } from './types';
import { ReportSummaryCard } from './ReportSummaryCard';
import { AccordionReportSection } from './AccordionReportSection';

const SECTION_ICONS: SectionIconMap = {
  claims: FileText,
  appointments: Calendar,
  treatments: Activity,
  specialists: UserCheck,
  events: Users,
  billing: CreditCard,
  payments: DollarSign,
  insurance: Shield,
  default: FileText,
};

interface ReportDashboardProps {
  title?: string;
  summaryCards?: SummaryCardData[];
  sections?: ReportSection[];
  type?: 'single' | 'multiple';
  collapsible?: boolean;
  defaultValue?: string | string[];
  onSummaryViewAll?: (card: SummaryCardData) => void;
  onSectionViewAll?: (sectionTitle?: string) => void;
  onRowClick?: (item: Record<string, any>) => void;
  className?: string;
}

const ReportDashboard: React.FC<ReportDashboardProps> = ({
  title,
  summaryCards = [],
  sections = [],
  type = 'multiple',
  collapsible = true,
  defaultValue,
  onSummaryViewAll,
  onSectionViewAll,
  onRowClick,
  className = '',
}) => {
  const validSections = sections.filter(s => s && s.title && s.data && s.columns);
  const accordionDefaultValue = defaultValue ?? (type === 'multiple' ? validSections.map(s => s.title) : undefined);

  return (
    <div className={cn('w-full max-w-7xl mx-auto space-y-6 font-sans', className)}>
      {title && <h1 className="text-2xl font-bold text-gray-900">{title}</h1>}

      {summaryCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {summaryCards.map((card, index) => (
            <ReportSummaryCard key={card.id || index} {...card} onViewAll={onSummaryViewAll} />
          ))}
        </div>
      )}

      {validSections.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
          <Accordion type={type} collapsible={collapsible} defaultValue={accordionDefaultValue} className="space-y-2">
            {validSections.map((section) => {
              const IconComponent = SECTION_ICONS[section.type] || SECTION_ICONS.default;
              return (
                <AccordionItem
                  key={section.title}
                  value={section.title}
                  className="border border-gray-200 bg-white shadow-sm rounded-lg"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full mr-4">
                      <div className="flex items-center gap-3">
                        <IconComponent className="h-5 w-5 text-gray-600" />
                        <h3 className="text-base font-semibold text-gray-900 text-left">
                          {section.title}
                        </h3>
                      </div>
                      {section.data.length > 0 && (
                        <span className="text-sm text-gray-500 ml-2">
                          {section.data.length} {section.data.length === 1 ? 'item' : 'items'}
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0">
                    <AccordionReportSection
                      data={section.data}
                      columns={section.columns}
                      sectionTitle={section.title}
                      maxItems={section.maxItems}
                      showViewAll={section.showViewAll}
                      onViewAll={onSectionViewAll}
                      onRowClick={onRowClick}
                    />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}
    </div>
  );
};

ReportDashboard.displayName = 'ReportDashboard';

export { ReportDashboard };
export type { ReportDashboardProps };
